import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Copy, ExternalLink, Plus, Trash2 } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useApp } from '../../context/AppContext';
import './Admin.css';

const FIELD_OPTIONS = [
  { key: 'pain_points', label: 'نقاط الألم', tag: 'tag-pain' },
  { key: 'user_needs', label: 'احتياجات المستخدم', tag: 'tag-need' },
  { key: 'opportunities', label: 'فرص التحسين', tag: 'tag-opp' },
  { key: 'key_quotes', label: 'اقتباسات مهمة', tag: 'tag-yellow' },
  { key: 'severity_score', label: 'درجة الخطورة', tag: 'tag-neutral' },
  { key: 'emotional_triggers', label: 'المحركات العاطفية', tag: 'tag-pain' },
  { key: 'behaviors', label: 'السلوكيات', tag: 'tag-neutral' },
];

export default function AgentBuilderPage() {
  const navigate = useNavigate();
  const { addAgent, getAgent, updateAgent } = useApp();
  const { agentId } = useParams();
  const existing = agentId ? getAgent(agentId) : null;

  const [form, setForm] = useState({
    name: existing?.name || '',
    objective: existing?.objective || '',
    prompt: existing?.prompt || '',
    language: existing?.language || 'ar',
    questionCount: existing?.questionCount || 7,
    outputFields: existing?.outputFields || ['pain_points', 'user_needs', 'opportunities', 'key_quotes', 'severity_score'],
  });

  const [saved, setSaved] = useState(null);
  const [copied, setCopied] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: undefined }));
  };

  const toggleField = (key) => {
    setForm(f => ({
      ...f,
      outputFields: f.outputFields.includes(key)
        ? f.outputFields.filter(k => k !== key)
        : [...f.outputFields, key],
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'اسم الوكيل مطلوب';
    if (!form.objective.trim()) e.objective = 'هدف البحث مطلوب';
    if (!form.prompt.trim()) e.prompt = 'تعليمات الوكيل مطلوبة';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    let agent;
    if (existing) {
      await updateAgent(existing.id, form);
      agent = { ...existing, ...form };
    } else {
      agent = await addAgent(form);
    }
    if (agent) setSaved(agent);
  };

  const shareLink = saved ? `${window.location.origin}/interview/${saved.id}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AdminLayout
      title={existing ? 'تعديل الوكيل' : 'وكيل مقابلة جديد'}
      subtitle="حدد أسئلة وأهداف البحث للوكيل"
      actions={
        <button className="btn btn-primary btn-sm" onClick={handleSave}>
          {saved ? '✓ تم الحفظ' : 'حفظ الوكيل'}
        </button>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '14px', alignItems: 'start' }}>
        {/* Left: form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div className="card">
            <h3 style={{ marginBottom: '1rem', color: 'var(--brown)' }}>إعدادات الوكيل</h3>

            <div className="form-group">
              <label className="form-label">اسم الوكيل *</label>
              <input
                className="form-input"
                value={form.name}
                onChange={e => update('name', e.target.value)}
                placeholder="مثال: تجربة البحث والاستكشاف"
                style={errors.name ? { borderColor: 'var(--red)' } : {}}
              />
              {errors.name && <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.name}</p>}
            </div>

            <div className="form-group">
              <label className="form-label">هدف البحث *</label>
              <textarea
                className="form-textarea"
                value={form.objective}
                onChange={e => update('objective', e.target.value)}
                placeholder="ما الذي تريد اكتشافه من هذه المقابلات؟"
                style={errors.objective ? { borderColor: 'var(--red)' } : {}}
              />
              {errors.objective && <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.objective}</p>}
            </div>

            <div className="form-grid" style={{ marginBottom: '1rem' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">اللغة</label>
                <select className="form-select" value={form.language} onChange={e => update('language', e.target.value)}>
                  <option value="ar">العربية</option>
                  <option value="en">الإنجليزية</option>
                  <option value="bilingual">ثنائي اللغة</option>
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">عدد الأسئلة</label>
                <select className="form-select" value={form.questionCount} onChange={e => update('questionCount', +e.target.value)}>
                  {[5, 7, 10, 12].map(n => <option key={n} value={n}>{n} أسئلة</option>)}
                </select>
              </div>
            </div>

            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">تعليمات الوكيل (System Prompt) *</label>
              <textarea
                className="form-textarea"
                style={{ minHeight: '130px', ...(errors.prompt ? { borderColor: 'var(--red)' } : {}) }}
                value={form.prompt}
                onChange={e => update('prompt', e.target.value)}
                placeholder="أنت [اسم الوكيل]، مساعد أبحاث متخصص. مهمتك هي..."
              />
              {errors.prompt && <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.prompt}</p>}
              <p style={{ fontSize: '11px', color: 'var(--brown-muted)', marginTop: '5px' }}>
                💡 اذكر اسم الوكيل، أسلوبه، وأنواع الأسئلة التي يجب أن يسألها
              </p>
            </div>
          </div>
        </div>

        {/* Right: output fields + link */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div className="card">
            <h3 style={{ marginBottom: '0.75rem', color: 'var(--brown)' }}>حقول المخرجات (JSON)</h3>
            <p style={{ fontSize: '12px', color: 'var(--brown-muted)', marginBottom: '1rem' }}>
              اختر ما يستخرجه الذكاء الاصطناعي تلقائياً بعد كل مقابلة
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {FIELD_OPTIONS.map(f => (
                <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.outputFields.includes(f.key)}
                    onChange={() => toggleField(f.key)}
                    style={{ accentColor: 'var(--brown)', width: '15px', height: '15px' }}
                  />
                  <span className={`tag ${f.tag}`}>{f.label}</span>
                  <code style={{ fontSize: '10px', color: 'var(--brown-muted)', fontFamily: 'var(--font-mono)' }}>{f.key}</code>
                </label>
              ))}
            </div>
          </div>

          {saved && (
            <div className="card card-yellow animate-in">
              <h3 style={{ marginBottom: '0.75rem', color: 'var(--brown)' }}>✓ تم إنشاء الوكيل</h3>
              <p style={{ fontSize: '12px', color: 'var(--brown-mid)', marginBottom: '0.75rem' }}>شارك هذا الرابط مع المشاركين:</p>
              <div className="link-box">{shareLink}</div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={copyLink}>
                  <Copy size={12} />
                  {copied ? 'تم النسخ!' : 'نسخ الرابط'}
                </button>
                <a href={shareLink} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ flex: 1 }}>
                  <ExternalLink size={12} />
                  معاينة
                </a>
              </div>
              <button className="btn btn-ghost btn-sm btn-full" style={{ marginTop: '8px' }} onClick={() => navigate('/admin/results')}>
                عرض النتائج
              </button>
            </div>
          )}

          {!saved && (
            <div className="card" style={{ background: 'var(--gray-card)' }}>
              <p style={{ fontSize: '12px', color: 'var(--brown-muted)', textAlign: 'center' }}>
                احفظ الوكيل أولاً للحصول على رابط المقابلة القابل للمشاركة
              </p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
